/**
 * Iter 85.1 / Phase E6 Sprint 1 — Workforce Activity Heartbeat (D41).
 *
 * Privacy-safe client heartbeat module. Strictly captures:
 *   • interaction_pulse (bool) — any input in the last window?
 *   • tab_visible      (bool) — is the tab in the foreground?
 *   • client_timestamp        — ISO 8601 (audit-metadata, D41-H)
 *   • heartbeat_version       — pinned (D41-H)
 *
 * NEVER captures, sends, or persists (D41-G):
 *   ✗ Passwords
 *   ✗ Keystroke content (key sequences, typed words)
 *   ✗ Screen captures or thumbnails
 *   ✗ Webcam, microphone, or any media frames
 *   ✗ URLs, referrers, page titles, tab titles, history
 *   ✗ Clipboard content
 *   ✗ File paths
 *   ✗ Mouse coordinates or movement vectors
 *   ✗ Device fingerprints (D41-H prohibits this explicitly)
 *
 * Mounted in App.js via <WorkforceHeartbeatMount />.
 */
import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export const HEARTBEAT_VERSION = "v1.0";
export const HEARTBEAT_CADENCE_MS = 60_000;        // D41-A — 60s
export const AUTO_IDLE_THRESHOLD_MS = 120_000;     // D41-B — 120s revised
const MIN_HEARTBEAT_INTERVAL_MS = 30_000;          // server-side throttle window

export default function WorkforceHeartbeatMount() {
    const { isAuthenticated } = useAuth();
    const interactionRef = useRef(false);
    const lastInteractionRef = useRef(0);
    const sentAtRef = useRef(0);
    const isIdleRef = useRef(false);

    useEffect(() => {
        if (!isAuthenticated) return;
        // Lazy-init (avoid Date.now() at render time → react-hooks/purity).
        lastInteractionRef.current = Date.now();

        // Coalesce: any input toggles the boolean flag; we DO NOT capture
        // keys, coordinates, target nodes, or any content.
        const onActivity = () => {
            interactionRef.current = true;
            lastInteractionRef.current = Date.now();
            if (isIdleRef.current) {
                // emit synthetic idle_end via a regular heartbeat — the
                // engine derives idle boundaries from interaction_pulse history.
                isIdleRef.current = false;
            }
        };

        // mousedown/keydown/scroll/touchstart — chosen for fidelity without
        // capturing content. We listen with passive:true so we never
        // intercept browser handling.
        const events = ["mousedown", "keydown", "scroll", "touchstart"];
        events.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));

        const sendHeartbeat = async () => {
            const now = Date.now();
            if (now - sentAtRef.current < MIN_HEARTBEAT_INTERVAL_MS) return;
            const pulse = interactionRef.current;
            interactionRef.current = false;        // reset for next window
            const tab_visible = !document.hidden;

            // D41-B — auto-idle if no interaction within 120s
            const sinceInteraction = now - lastInteractionRef.current;
            if (sinceInteraction > AUTO_IDLE_THRESHOLD_MS) {
                isIdleRef.current = true;
            }

            try {
                await api.post("/workforce/heartbeat", {
                    interaction_pulse: pulse,
                    tab_visible,
                    client_timestamp: new Date().toISOString(),
                    heartbeat_version: HEARTBEAT_VERSION,
                });
                sentAtRef.current = now;
            } catch (err) {
                // Silent fail — heartbeat is best-effort. Most common
                // failures: 400 (user not employee-linked), 429 (throttle).
            }
        };

        const intervalId = setInterval(sendHeartbeat, HEARTBEAT_CADENCE_MS);
        // Send one immediately on mount
        sendHeartbeat();

        return () => {
            clearInterval(intervalId);
            events.forEach(ev => window.removeEventListener(ev, onActivity));
        };
    }, [isAuthenticated]);

    return null;   // headless component
}
